<script lang="ts" setup>
  import { UseBemProps } from '~/composables/Bem/BemFacetOptions'
  import useBem from '~/composables/Bem/Bem'
  import { MatchDraggableEntry } from '~/model/TaskMatch/TaskMatch'
  import { computed } from '#imports'
  import useDropZone, { DropZonePayload, UseDropZoneEmits, UseDropZoneProps } from '~/composables/DropZone/DropZone'

  interface Props extends UseBemProps, UseDropZoneProps {
    facets?: Array<string>
    dropZoneId: string
    draggableItemList: Array<MatchDraggableEntry>
    numSlots: number
  }

  interface Emits extends UseDropZoneEmits {
    (e: 'match', payload: DropZonePayload): void
  }

  const props = defineProps<Props>()
  const emit = defineEmits<Emits>()
  const { handleDrop, isDraggingOver, toggleDraggingOver } = useDropZone(props, emit)
  const { bemFacets, bemAdd } = useBem('c-match-drop-zone', props, {})

  const remainingSlots = computed<number>(() => {
    return props.numSlots - props.draggableItemList.length
  })
  const rootClasses = computed<Array<string>>(() => {
    return [
      ...bemFacets.value,
      bemAdd(isDraggingOver.value && remainingSlots.value > 0 ? 'is-dragging-over' : ''),
    ]
  })

  const onDrop = (e: DragEvent) => {
    if (remainingSlots.value <= 0) {
      e.preventDefault()
      return
    }

    handleDrop(e)
  }
</script>

<template>
  <div :class="rootClasses" class="c-match-drop-zone">
    <div
      class="c-match-drop-zone__box"
      @dragleave="toggleDraggingOver(false)"
      @drop="onDrop($event)"
      @dragover.prevent
      @dragenter.prevent="toggleDraggingOver(true)"
    >
      <div v-if="$slots.title" class="c-match-drop-zone__header">
        <slot name="title" />
      </div>
      <ul class="c-match-drop-zone__items-list u-reset">
        <li
          v-for="draggableItem in draggableItemList"
          :key="draggableItem.id"
          class="c-match-drop-zone__items-entry"
        >
          <div class="c-match-drop-zone__positionner">
            <slot name="content" v-bind="draggableItem" />
          </div>
        </li>
        <li v-for="n in remainingSlots" :key="n" class="c-match-drop-zone__items-entry">
          <div class="c-match-drop-zone__positionner" />
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @use 'sass:math';
  @use '@nirazul/scss-utils' as utils;
  @use '/assets/scss/util/color/color' as col;
  @use '/assets/scss/util/transition' as trs;
  @use '/assets/scss/util/anaglyph/anaglyph' as anaglyph;

  $offset: 4px;
  $aspect-ratio: math.div(16, 9);

  .c-match-drop-zone {
    display: flex;
    flex: 0 1 100%;
    padding: 0 $offset $offset 0;
  }

  .c-match-drop-zone--with-image {
    .c-match-drop-zone__header {
      height: auto;
      justify-content: flex-start;
      text-align: left;
      column-gap: 8px;
    }
  }

  .c-match-drop-zone--is-dragging-over {
    .c-match-drop-zone__header {
      background-color: col.$brand-cyan;
    }
  }

  .c-match-drop-zone--is-text-only {
    .c-match-drop-zone__items-entry {
      padding-top: 40px;
    }
  }

  .c-match-drop-zone--is-image-only {
    .c-match-drop-zone__items-entry {
      padding-top: math.div(100%, $aspect-ratio);
    }
  }

  .c-match-drop-zone__box {
    @include anaglyph.with-shadow-apply(mono, $offset, $offset, 0, 0, col.$variant-red-dark);
    pointer-events: auto;
    display: flex;
    flex-flow: column nowrap;
    flex: 0 1 100%;
  }

  .c-match-drop-zone__header {
    pointer-events: none;
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-items: center;
    flex: 0 1 100%;
    padding: 4px 4px;
    color: col.$monochrome-black;
    background-color: col.$variant-cyan-light;
    text-align: center;
    transition: background-color trs.$default;
  }

  .c-match-drop-zone__items-list {
    pointer-events: none;
    background-color: col.$variant-red-light;
    background-image: //
      linear-gradient(to right, col.$variant-red-light 8px, transparent 1px),
      linear-gradient(rgba(col.$variant-red-dark, 0.2) 1px, transparent 1px);
    background-size: 20px calc(100% / v-bind(numSlots) + 1px);
    background-position: 0 -1px;
  }

  .c-match-drop-zone__items-entry {
    position: relative;
    width: 100%;
  }

  .c-match-drop-zone__positionner {
    @include utils.overlay;
  }
</style>
